import React, { createContext, useCallback, useEffect, useState } from 'react';
import {
  signInWithChallenge,
  completePendingNewPassword,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  resetPassword as cognitoResetPassword,
  confirmResetPassword as cognitoConfirmResetPassword,
  resendSignUpCode as cognitoResendSignUpCode,
  signOut as cognitoSignOut,
  getCurrentAuthUser,
  updateCognitoAttributes,
  refreshCognitoSession,
} from '../lib/cognitoAuth';

import Loading from '../Components/layouts/Loading';
import { callNuatalker } from '../lib/nuaApi';
import { useNavigate } from 'react-router-dom';
import {
  canAccessAllAccounts as resolveCanAccessAllAccounts,
  isAccountId,
} from '../constants/permissions';
import { resetAuditCallerHeadersCache } from '../services/auditCallerHeaders';
import { clearIamCredentials } from '../lib/amplifyTokenProvider';

/**
 * Authentication context for managing user authentication state and operations.
 * @constant
 * @type {Object}
 * */
export const AuthContext = createContext(null);

const getHttpStatus = (error) =>
  error?.status ??
  error?.response?.statusCode ??
  error?.$metadata?.httpStatusCode ??
  error?._response?.statusCode ??
  error?.statusCode ??
  error?.originalError?.response?.statusCode;

const isUnauthorizedError = (error) => getHttpStatus(error) === 401;

/**
 * Authentication Provider Component that manages user authentication state and operations
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [backoffice, setBackoffice] = useState(false);
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [userSub, setUserSub] = useState(null);
  const [meAccounts, setMeAccounts] = useState(null);
  const navigate = useNavigate();
  const isAuthenticated = !!user;

  const resetMeAccounts = () => {
    setAccounts([]);
    setBackoffice(false);
    setPlatformAdmin(false);
    setUserSub(null);
    setMeAccounts(null);
  };

  const applyMeAccounts = (payload) => {
    const data = payload && typeof payload === 'object' ? payload : {};
    setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
    setBackoffice(Boolean(data.backoffice));
    setPlatformAdmin(Boolean(data.platformAdmin));
    setUserSub(data.userSub ?? null);
    setMeAccounts(payload ?? null);
  };

  const goToLogin = () => {
    localStorage.removeItem('sessionToken');
    setUser(null);
    resetMeAccounts();
    navigate('/auth/login');
  };

  const handleError = (error) => {
    const message = error?.message || '';
    if (message.includes('NotAuthorizedException')) {
      return 'Usuario o contraseña incorrectos. Inténtalo de nuevo.';
    }
    if (message.includes('UserNotFoundException')) {
      return 'Ese usuario no existe. Revisa el correo.';
    }
    if (message.includes('UserNotConfirmedException')) {
      return 'Tu cuenta no está confirmada. Revisa el correo de verificación.';
    }
    if (message.includes('PasswordResetRequiredException')) {
      return 'Tienes que restablecer la contraseña.';
    }
    if (
      message.includes('verified email') ||
      message.includes('verified phone_number')
    ) {
      return 'Tu correo aún no está verificado. Entra al menos una vez con la contraseña temporal antes de recuperarla.';
    }
    return message || 'Ocurrió un error en la autenticación.';
  };

  /**
   * Logs out current user
   * @async
   * @function logout
   * @returns {Promise<Object>} Logout operation result
   */
  const logout = async () => {
    try {
      cognitoSignOut();
      resetAuditCallerHeadersCache();
      void clearIamCredentials();
      goToLogin();
      return { success: true };
    } catch (error) {
      cognitoSignOut();
      resetAuditCallerHeadersCache();
      void clearIamCredentials();
      goToLogin();
      return { success: false, error: handleError(error) };
    }
  };

  /**
   * Fetches GET /me/accounts and persists membership flags.
   * 401 → logout. Any other error → empty accounts so the user can still land on /accounts.
   */
  const refreshMeAccounts = async () => {
    try {
      const payload = await callNuatalker({
        path: '/me/accounts',
        method: 'GET',
      });
      applyMeAccounts(payload);
      return { ok: true, payload };
    } catch (error) {
      console.error('Error fetching /me/accounts:', error);
      if (isUnauthorizedError(error)) {
        await logout();
        return { ok: false, unauthorized: true };
      }
      resetMeAccounts();
      return { ok: false, unauthorized: false };
    }
  };

  /**
   * Checks and sets the current authenticated user
   * @async
   * @function checkUser
   * @returns {Promise<void>}
   */
  const checkUser = async () => {
    setLoading(true);
    try {
      const nextUser = await getCurrentAuthUser();
      if (!nextUser) {
        throw new Error('No authenticated user');
      }
      const nextJwt = nextUser.user.idToken.jwtToken;
      setUser((prev) =>
        prev?.user?.idToken?.jwtToken === nextJwt ? prev : nextUser
      );
      await refreshMeAccounts();
    } catch (error) {
      goToLogin();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Authenticates a user with username and password
   * @async
   * @function login
   * @param {string} username - User's email/username
   * @param {string} password - User's password
   * @returns {Promise<Object>} Authentication result object
   */
  const login = async (username, password) => {
    try {
      const { needsNewPassword } = await signInWithChallenge(username, password);

      if (needsNewPassword) {
        return {
          success: true,
          requiresPasswordChange: true,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: { pending: true },
        };
      }

      const sessionUser = await getCurrentAuthUser();
      if (!sessionUser) {
        throw new Error('No se pudo establecer la sesión');
      }
      setUser(sessionUser);
      const meResult = await refreshMeAccounts();
      if (meResult?.unauthorized) {
        return { success: false, error: 'Sesión no autorizada.' };
      }
      const payload = meResult?.payload || {};
      return {
        success: true,
        user: sessionUser,
        accounts: Array.isArray(payload.accounts) ? payload.accounts : [],
        backoffice: Boolean(payload.backoffice),
        platformAdmin: Boolean(payload.platformAdmin),
        lastAccountId: sessionUser.attributes?.['custom:hubspotid'] || null,
        canAccessAllAccounts: resolveCanAccessAllAccounts({
          backoffice: payload.backoffice,
          platformAdmin: payload.platformAdmin,
          user: sessionUser,
        }),
      };
    } catch (error) {
      return {
        success: false,
        error: handleError(error),
      };
    }
  };

  /**
   * Registers a new user
   * @async
   * @function register
   * @param {string} username - User's email/username
   * @param {string} password - User's password
   * @param {string} customerId - HubSpot customer ID
   * @returns {Promise<Object>} Registration result object
   */
  const register = async (username, password, customerId, departments = '*') => {
    try {
      if (!customerId || typeof customerId !== 'string') {
        throw new Error(
          'El ID de HubSpot es obligatorio y debe ser un string.'
        );
      }
      const response = await cognitoSignUp({
        username,
        password,
        customerId,
        departments,
      });
      return { success: true, response };
    } catch (error) {
      return { success: false, error: handleError(error) };
    }
  };

  /**
   * Sends verification code to user's email
   * @async
   * @function sendVerificationCode
   * @param {string} username - User's email/username
   * @returns {Promise<Object>} Operation result object
   */
  const sendVerificationCode = async (username) => {
    try {
      await cognitoResendSignUpCode(username);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleError(error) };
    }
  };

  /**
   * Verifies user account with confirmation code
   * @async
   * @function verifyAccount
   * @param {string} username - User's email/username
   * @param {string} code - Verification code
   * @returns {Promise<Object>} Verification result object
   */
  const verifyAccount = async (username, code) => {
    try {
      await cognitoConfirmSignUp(username, code);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleError(error) };
    }
  };

  /**
   * Initiates password reset process
   * @async
   * @function restorePassword
   * @param {string} username - User's email/username
   * @returns {Promise<Object>} Password reset initiation result
   */
  const restorePassword = async (username) => {
    try {
      await cognitoResetPassword(username);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleError(error) };
    }
  };

  /**
   * Confirms password reset with verification code
   * @async
   * @function confirmRestorePassword
   * @param {string} username - User's email/username
   * @param {string} code - Verification code
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password reset confirmation result
   */
  const confirmRestorePassword = async (username, code, newPassword) => {
    try {
      await cognitoConfirmResetPassword(username, code, newPassword);
      return { success: true };
    } catch (error) {
      return { success: false, error: handleError(error) };
    }
  };

  /**
   * Updates user password for temporary password scenario
   * @async
   * @function updateUserPassword
   * @param {string} newPassword - New password
   * @param {Object} session - Session data from login
   * @returns {Promise<Object>} Password update result
   */
  const updateUserPassword = async (newPassword) => {
    try {
      await completePendingNewPassword(newPassword);
      const sessionUser = await getCurrentAuthUser();
      if (!sessionUser) {
        throw new Error('No se pudo establecer la sesión');
      }
      setUser(sessionUser);
      const meResult = await refreshMeAccounts();
      if (meResult?.unauthorized) {
        return { success: false, error: 'Sesión no autorizada.' };
      }
      return {
        success: true,
        lastAccountId: sessionUser.attributes?.['custom:hubspotid'] || null,
      };
    } catch (error) {
      console.error('Error in updateUserPassword:', error);
      return { success: false, error: handleError(error) };
    }
  };

  const canAccessAllAccounts = resolveCanAccessAllAccounts({
    backoffice,
    platformAdmin,
    user,
  });

  const rememberLastAccount = useCallback(async (hubSpotId) => {
    const id = String(hubSpotId || '').trim();
    if (!isAccountId(id)) return;
    if (String(user?.attributes?.['custom:hubspotid'] || '') === id) return;
    try {
      await updateCognitoAttributes({ 'custom:hubspotid': id });
      // El idToken lleva `custom:hubspotid` como claim: sin refresh se queda
      // el portal anterior y el backend responde 403.
      await refreshCognitoSession();
      setUser((prev) =>
        prev
          ? {
              ...prev,
              attributes: { ...prev.attributes, 'custom:hubspotid': id },
            }
          : prev
      );
    } catch (error) {
      console.error('Error remembering last account:', error);
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accounts,
        backoffice,
        platformAdmin,
        canAccessAllAccounts,
        userSub,
        meAccounts,
        isAuthenticated,
        loading,
        login,
        register,
        sendVerificationCode,
        verifyAccount,
        restorePassword,
        confirmRestorePassword,
        updateUserPassword,
        rememberLastAccount,
        logout,
        refreshMeAccounts,
      }}
    >
      {loading ? <Loading /> : children}
    </AuthContext.Provider>
  );
};
